/**
 * CommandPalette — per docs/specs/README.md
 * Global ⌘K command palette. Searches ROUTES + a small list of quick actions,
 * supports keyboard navigation (↑/↓/Enter/Esc) and mouse interaction.
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-19-v1
 */
import { useEffect, useMemo, useRef, useState, type ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, Command as CommandIcon, RefreshCw, Search, ToggleRight } from 'lucide-react';
import { ROUTES, SECTION_LABELS, type RouteDef } from '../../lib/routes';

interface PaletteItem {
    key: string;
    label: string;
    section: string;
    sectionLabel: string;
    icon: ComponentType<{ size?: number | string }>;
    onRun: () => void;
}

const QUICK_ACTIONS_SECTION = 'actions';

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    // Build the items pool. Quick actions are always present; route items drop
    // in from ROUTES untouched.
    const allItems = useMemo<PaletteItem[]>(() => {
        const routeItems: PaletteItem[] = ROUTES.map((r: RouteDef) => ({
            key: `route:${r.path}`,
            label: r.label,
            section: r.section,
            sectionLabel: SECTION_LABELS[r.section],
            icon: r.icon as ComponentType<{ size?: number | string }>,
            onRun: () => {
                navigate(r.path);
                onClose();
            }
        }));
        const quickActions: PaletteItem[] = [
            {
                key: 'action:refresh-queries',
                label: 'Refresh all queries',
                section: QUICK_ACTIONS_SECTION,
                sectionLabel: 'Actions',
                icon: RefreshCw,
                onRun: () => onClose()
            },
            {
                key: 'action:toggle-env',
                label: 'Toggle environment indicator',
                section: QUICK_ACTIONS_SECTION,
                sectionLabel: 'Actions',
                icon: ToggleRight,
                onRun: () => onClose()
            },
            {
                key: 'action:open-help',
                label: 'Open help docs \u2197',
                section: QUICK_ACTIONS_SECTION,
                sectionLabel: 'Actions',
                icon: ArrowUpRight,
                onRun: () => onClose()
            }
            // Note: JS string literals support \u escapes — the arrow renders as ↗.
        ];
        return [...quickActions, ...routeItems];
    }, [navigate, onClose]);

    // Filter items by case-insensitive substring on label + section.
    const filtered = useMemo<PaletteItem[]>(() => {
        const q = query.trim().toLowerCase();
        if (!q) return allItems;
        return allItems.filter((it) => {
            const hay = `${it.label} ${it.sectionLabel}`.toLowerCase();
            return hay.includes(q);
        });
    }, [allItems, query]);

    // Reset selection/query when opened; focus the input.
    useEffect(() => {
        if (open) {
            setQuery('');
            setSelected(0);
            // Defer focus one frame so the input is in the DOM.
            requestAnimationFrame(() => inputRef.current?.focus());
        }
    }, [open]);

    // Clamp selection when the filtered list shrinks.
    useEffect(() => {
        if (selected >= filtered.length) setSelected(Math.max(0, filtered.length - 1));
    }, [filtered, selected]);

    // Scroll selected row into view.
    useEffect(() => {
        if (!open) return;
        const el = listRef.current?.querySelector<HTMLElement>(
            `[data-palette-index="${selected}"]`
        );
        el?.scrollIntoView({ block: 'nearest' });
    }, [selected, open]);

    if (!open) return null;

    const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelected((s) => (filtered.length === 0 ? 0 : (s + 1) % filtered.length));
            return;
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelected((s) =>
                filtered.length === 0 ? 0 : (s - 1 + filtered.length) % filtered.length
            );
            return;
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            const it = filtered[selected];
            if (it) it.onRun();
            return;
        }
    };

    return (
        <div
            className="palette-backdrop"
            onClick={onClose}
            role="presentation"
            onKeyDown={onKeyDown}
        >
            <div
                className="palette"
                role="dialog"
                aria-modal="true"
                aria-label="Command palette"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={onKeyDown}
            >
                <div className="palette-input-row">
                    <Search size={14} />
                    <input
                        ref={inputRef}
                        className="palette-input"
                        type="text"
                        placeholder="Search pages, actions…"
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setSelected(0);
                        }}
                        aria-label="Command palette search"
                        autoComplete="off"
                        spellCheck={false}
                    />
                    <kbd className="palette-kbd">ESC</kbd>
                </div>

                <div className="palette-list" ref={listRef} role="listbox">
                    {filtered.length === 0 && (
                        <div className="palette-empty">
                            <CommandIcon size={16} />
                            <span>No matches for &ldquo;{query}&rdquo;</span>
                        </div>
                    )}
                    {filtered.map((it, i) => {
                        const Icon = it.icon;
                        const isSelected = i === selected;
                        return (
                            <button
                                key={it.key}
                                type="button"
                                className="palette-row"
                                data-palette-index={i}
                                data-selected={isSelected ? 'true' : undefined}
                                role="option"
                                aria-selected={isSelected}
                                onMouseEnter={() => setSelected(i)}
                                onClick={() => it.onRun()}
                            >
                                <span className="palette-row-icon">
                                    <Icon size={14} />
                                </span>
                                <span className="palette-row-label">{it.label}</span>
                                <span className="palette-row-section">{it.sectionLabel}</span>
                            </button>
                        );
                    })}
                </div>

                <div className="palette-hint">
                    <span className="palette-hint-group">
                        <kbd className="palette-kbd">{'\u2191'}</kbd>
                        <kbd className="palette-kbd">{'\u2193'}</kbd>
                        <span>navigate</span>
                    </span>
                    <span className="palette-hint-group">
                        <kbd className="palette-kbd">{'\u21B2'}</kbd>
                        <span>open</span>
                    </span>
                    <span className="palette-hint-sep" />
                    <span className="palette-hint-group">
                        <span>press</span>
                        <kbd className="palette-kbd">?</kbd>
                        <span>for all shortcuts</span>
                    </span>
                </div>
            </div>
        </div>
    );
}
